import { Controller } from "@hotwired/stimulus"

// Generic hover popover controller with lazy loading.
//
// On first hover the content is fetched from `url` value and cached in memory.
// Subsequent hovers use the cache — no extra requests.
// Uses position:fixed so it escapes overflow:hidden/auto containers (tables, drawers).
//
// Usage:
//   <div data-controller="popover" data-popover-url-value="/path/to/content">
//     <a data-popover-target="trigger"
//        data-action="mouseenter->popover#triggerMouseenter mouseleave->popover#triggerMouseleave">
//       Hover me
//     </a>
//     <div class="hidden"
//          data-popover-target="content"
//          data-action="mouseenter->popover#contentMouseenter mouseleave->popover#contentMouseleave">
//     </div>
//   </div>
export default class extends Controller {
  static targets = ["trigger", "content"]
  static values = { url: String }

  #showTimer = null
  #hideTimer = null
  #cachedHTML = null
  #loading = false

  triggerMouseenter() {
    clearTimeout(this.#hideTimer)
    this.#showTimer = setTimeout(() => {
      if (this.#cachedHTML) {
        this.#showCached()
      } else if (this.hasUrlValue) {
        this.#fetch()
      }
    }, 200)
  }

  triggerMouseleave() {
    clearTimeout(this.#showTimer)
    this.#hideTimer = setTimeout(() => this.#hide(), 150)
  }

  contentMouseenter() {
    clearTimeout(this.#hideTimer)
  }

  contentMouseleave() {
    this.#hideTimer = setTimeout(() => this.#hide(), 150)
  }

  stopClick(event) {
    event.stopPropagation()
  }

  async #fetch() {
    if (this.#loading) return
    this.#loading = true

    // Show a skeleton card immediately so the user gets visual feedback
    this.contentTarget.innerHTML = `
      <div class="card card-border bg-base-100 shadow-xl border-base-300 w-80">
        <div class="card-body gap-2 p-4">
          <div class="skeleton h-4 w-28"></div>
          <div class="skeleton h-3 w-full"></div>
          <div class="skeleton h-3 w-4/5"></div>
          <div class="skeleton h-3 w-full mt-1"></div>
          <div class="skeleton h-3 w-3/4"></div>
          <div class="skeleton h-3 w-full"></div>
        </div>
      </div>`
    this.#position()

    try {
      const response = await fetch(this.urlValue, {
        headers: { "Accept": "text/html", "X-Requested-With": "XMLHttpRequest" }
      })

      if (response.ok) {
        this.#cachedHTML = await response.text()
        // Only update if the user is still hovering
        if (!this.contentTarget.classList.contains("hidden")) {
          this.contentTarget.innerHTML = this.#cachedHTML
          this.#position() // Reposition now we have real content dimensions
        }
      } else {
        this.#hide()
      }
    } catch {
      this.#hide()
    } finally {
      this.#loading = false
    }
  }

  #showCached() {
    this.contentTarget.innerHTML = this.#cachedHTML
    this.#position()
  }

  #position() {
    const content = this.contentTarget
    const rect = this.triggerTarget.getBoundingClientRect()
    const gap = 8

    // Move off-screen first so we can measure natural dimensions
    content.style.position = "fixed"
    content.style.zIndex = "9999"
    content.style.top = "-9999px"
    content.style.left = "-9999px"
    content.classList.remove("hidden")

    const cw = content.offsetWidth
    const ch = content.offsetHeight
    const vw = window.innerWidth
    const vh = window.innerHeight

    // Default: open to the right of the trigger
    let top = rect.top
    let left = rect.right + gap

    // Flip to the left if not enough room on the right
    if (left + cw > vw - gap) left = rect.left - cw - gap

    // Shift up if the bottom edge would overflow
    if (top + ch > vh - gap) top = vh - ch - gap

    content.style.top = `${Math.max(gap, top)}px`
    content.style.left = `${Math.max(gap, left)}px`
  }

  #hide() {
    this.contentTarget.classList.add("hidden")
    this.contentTarget.style.top = ""
    this.contentTarget.style.left = ""
  }
}
